import { ref, computed, watch } from "vue";
import { defineStore, acceptHMRUpdate } from "pinia";
import { markRaw } from "vue";
import manifest from "@/components/notifications/manifest";
import BaseNotification from "@/components/notifications/BaseNotification.vue";
import { holding as firstOpenHolding } from "@/utils/firstOpenGate";

// The groupKeys that have toasted under `oncePerSession` (app-design-standard
// D18). The session is the page session: module state, so a reload clears it
// and no store reset does. A notice for a STANDING condition toasts on its
// first arrival and is dropped after that, where the coalescer would only
// collapse the repeats that land while the toast is still on screen.
const toastedOncePerSession = new Set();

// Test-only reset — the Set outlives a fresh Pinia, so a suite clears it
// between cases.
export function __resetOncePerSessionForTests() {
  toastedOncePerSession.clear();
}

export const useNotificationsStore = defineStore("notifications", () => {
  const activeNotifications = ref([]);
  const position = ref('bottom-left');
  // Cap on visible toasts in NotificationsOverlay (bead th-tyd0k). The full
  // list still lives in activeNotifications — activeCount remains the full
  // count for the Artemis envelope. The overlay clips rendering to the most
  // recent maxVisible toasts and surfaces the remainder via a "+N more" chip.
  const maxVisible = ref(3);

  // Count-only accessor for Artemis client_state envelope.
  // Exposes the number of active toast notifications; intentionally does NOT
  // expose notification body text (titles, messages) — see spec
  // artemis-client-state-envelope: ephemeral.notifications_active is { count: integer }.
  const activeCount = computed(() => activeNotifications.value.length);

  // The first-open gate (launch-marketing-pages design M11). Every pusher
  // funnels through push(), which queues the entry here while the gate holds.
  // Its id is already minted, so every caller still gets it at once. On
  // release the queue flushes in order, each toast pushed then, so its
  // auto-hide counts from the release.
  const heldNotifications = [];

  function push(entry) {
    if (firstOpenHolding.value) heldNotifications.push(entry);
    else activeNotifications.value.push(entry);
    return entry.id;
  }

  watch(
    firstOpenHolding,
    (isHolding) => {
      if (!isHolding) activeNotifications.value.push(...heldNotifications.splice(0));
    },
    { flush: "sync" },
  );

  // Coalesce by groupKey (bead th-5c153). When a new toast carries a groupKey
  // that matches an existing entry, the existing entry is mutated in place:
  //   - count is bumped
  //   - title / message / autoHideSeconds / per-instance props are replaced
  //     with the new values (latest-wins semantics — onClick handlers and
  //     navigation targets get refreshed so the toast points at the most
  //     recent triggering action)
  //   - bumpSignal is incremented so BaseNotification re-arms its hide timer
  //
  // Callers opt in by providing groupKey (either via manifest entry or via
  // the per-call props/additionalProps). No groupKey → no coalescing, every
  // call adds a new entry (preserves existing behavior for toasts that fire
  // per-entity and should not collapse).
  //
  // A held toast coalesces too: the gate queues entries after the grouping,
  // so a repeat that arrives while it holds merges into the queued entry.
  function findGroupedNotification(groupKey) {
    if (!groupKey) return null;
    const matches = (n) => n.props && n.props.groupKey === groupKey;
    return activeNotifications.value.find(matches) ?? heldNotifications.find(matches) ?? null;
  }

  // Public peek for callers that need to compose a message based on the
  // current coalesced count BEFORE enqueueing the next merge. Used by the
  // Artemis completion-batch path to render "Artemis completed N threads"
  // (bead th-5c153). Returns the existing entry or null — callers should
  // treat the result as read-only and re-enqueue via addNotification /
  // addDataProcessingNotification rather than mutating it directly.
  function peekGrouped(groupKey) {
    return findGroupedNotification(groupKey);
  }

  function mergeIntoExisting(existing, nextProps) {
    const nextCount = (existing.props.count || 1) + 1;
    // Replace per-instance props (title, message, onClick, threadId, etc.)
    // with the latest values; preserve identity fields (id, type, groupKey).
    Object.assign(existing.props, nextProps, {
      id: existing.props.id,
      type: existing.props.type,
      groupKey: existing.props.groupKey,
      count: nextCount,
      bumpSignal: (existing.props.bumpSignal || 0) + 1,
    });
    return existing.id;
  }

  // Per-call `overrides` lets dynamic toasts (e.g. mention notifications) supply
  // interpolated title/message strings without bloating the static manifest.
  // Manifest entry still provides `type`, `actions`, and the fallback
  // title/message; overrides win when provided. Existing call sites that omit
  // the 4th arg get the manifest values unchanged.
  //
  // `props.oncePerSession: true` toasts the call's groupKey at most once per
  // page session: the first arrival toasts as any other, and a later one
  // returns before the coalescer, so nothing shows and no count grows. The
  // flag is the store's own and never reaches the toast's props. Without a
  // groupKey there is nothing to remember, and the call toasts as usual.
  function addNotification(id, autoHideSeconds = 3, props = {}, overrides = {}) {
    const entry = manifest[id];
    if (!entry) return;

    const resolvedAutoHide =
      autoHideSeconds || entry.autoHideSeconds || 3;
    // groupKey resolution: caller props > overrides > manifest declaration.
    // Manifest entries opt their toast in to coalescing by declaring groupKey
    // statically (e.g. inboxNotification → 'inboxNotification'); callers can
    // refine the key per-instance (e.g. 'inboxNotification:<kind>' to coalesce
    // per notification kind).
    const groupKey = props.groupKey ?? overrides.groupKey ?? entry.groupKey;

    const { oncePerSession, ...instanceProps } = props;
    if (oncePerSession && groupKey) {
      if (toastedOncePerSession.has(groupKey)) return;
      toastedOncePerSession.add(groupKey);
    }

    // Random suffix prevents id collisions when callers fire multiple
    // notifications in the same millisecond (tight loops, Promise.all
    // bursts). removeNotification + visible-window selection both key on
    // unique ids — collisions cause silent removal of the wrong entry.
    const instanceId = `${id}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    const baseProps = {
      id: instanceId,
      type: entry.type,
      title: overrides.title ?? entry.title,
      message: overrides.message ?? entry.message,
      autoHideSeconds: resolvedAutoHide,
      actions: entry.actions || undefined,
      ...instanceProps,
      groupKey,
    };

    if (groupKey) {
      const existing = findGroupedNotification(groupKey);
      if (existing) return mergeIntoExisting(existing, baseProps);
    }

    return push({
      id: instanceId,
      component: markRaw(BaseNotification),
      props: { ...baseProps, count: 1, bumpSignal: 0 },
    });
  }

  // Enhanced method for BaseNotification with data processing updates.
  // Imperative path: callers must supply groupKey explicitly via
  // additionalProps when they want coalescing. No implicit grouping —
  // arbitrary type/title/message tuples shouldn't auto-merge.
  function addDataProcessingNotification(type, title, message, autoHideSeconds = 5, additionalProps = {}) {
    const groupKey = additionalProps.groupKey;

    const instanceId = `data_processing_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    const baseProps = {
      id: instanceId,
      type,
      title,
      message,
      autoHideSeconds,
      ...additionalProps,
      groupKey,
    };

    if (groupKey) {
      const existing = findGroupedNotification(groupKey);
      if (existing) return mergeIntoExisting(existing, baseProps);
    }

    return push({
      id: instanceId,
      component: markRaw(BaseNotification),
      props: { ...baseProps, count: 1, bumpSignal: 0 },
    });
  }

  // Convenience methods for common data processing notifications.
  // Each status gets its own stable groupKey so a flood of N concurrent
  // operations produces ONE toast with a ×N count badge instead of N stacked
  // toasts. processing / completed / error don't share keys — a completion
  // doesn't merge into a still-active processing toast.
  function addBusinessOperationNotification(status, message, additionalProps = {}) {
    const notificationConfig = {
      'processing': {
        type: 'info',
        title: 'Processing operation',
        autoHideSeconds: null
      },
      'completed': {
        type: 'success',
        title: 'Operation completed',
        autoHideSeconds: 5
      },
      'error': {
        type: 'error',
        title: 'Operation failed',
        autoHideSeconds: 8
      }
    };

    const config = notificationConfig[status] || notificationConfig['error'];
    return addDataProcessingNotification(
      config.type,
      config.title,
      message,
      config.autoHideSeconds,
      { groupKey: `business-operation:${status}`, ...additionalProps },
    );
  }

  // Remove notification by ID (useful for updating processing notifications).
  // A toast the first-open gate still holds is withdrawn from its queue.
  function removeNotification(notificationId) {
    const index = activeNotifications.value.findIndex(n => n.id === notificationId);
    if (index !== -1) {
      activeNotifications.value.splice(index, 1);
      return;
    }
    const heldIndex = heldNotifications.findIndex(n => n.id === notificationId);
    if (heldIndex !== -1) heldNotifications.splice(heldIndex, 1);
  }

  return {
    activeNotifications,
    activeCount,
    position,
    maxVisible,
    addNotification,
    addDataProcessingNotification,
    addBusinessOperationNotification,
    removeNotification,
    peekGrouped
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useNotificationsStore, import.meta.hot),
  );
}
